const TYPE_ORDER = ['asset', 'liability', 'equity', 'revenue', 'other_income', 'cost', 'expense', 'other_expense'] as const;

const TYPE_CODE_RANGE: Record<string, { start: number; end: number }> = {
  asset: { start: 10000, end: 19999 },
  liability: { start: 20000, end: 29999 },
  equity: { start: 30000, end: 39999 },
  revenue: { start: 40000, end: 49999 },
  cost: { start: 50000, end: 59999 },
  expense: { start: 60000, end: 79999 },
  other_income: { start: 80000, end: 89999 },
  other_expense: { start: 90000, end: 99999 },
};

interface Account {
  account_code: string;
  account_type: string;
  parent_code: string | null;
}

export function buildRenumberMappings(accounts: Account[]): { old_code: string; new_code: string }[] {
  if (accounts.length === 0) return [];

  const oldToNew = new Map<string, string>();

  for (const type of TYPE_ORDER) {
    const typeAccounts = accounts.filter(a => (a.account_type || 'expense') === type);
    if (typeAccounts.length === 0) continue;

    const range = TYPE_CODE_RANGE[type] || { start: 60000, end: 79999 };

    // Group by actual parent_code from data
    const childrenByParent = new Map<string, Account[]>();
    for (const a of typeAccounts) {
      const key = a.parent_code || '__root__';
      const arr = childrenByParent.get(key) || [];
      arr.push(a);
      childrenByParent.set(key, arr);
    }

    // Determine which accounts are actual parents (have children pointing to them)
    const parentCodes = new Set<string>();
    for (const a of typeAccounts) {
      if (a.parent_code) parentCodes.add(a.parent_code);
    }

    const getChildMultiplier = (parentCode: string): number => {
      const zeros = (parentCode.match(/0+$/) || [''])[0].length;
      if (zeros >= 4) return 1000;
      if (zeros === 3) return 100;
      return 1;
    };

    let parentSlot = 0;
    let leafSlot = 0;

    // Recursively renumber a parent and its descendants
    const renumber = (oldParentCode: string | null, newParentCode: string) => {
      const key = oldParentCode || '__root__';
      const siblings = childrenByParent.get(key) || [];
      const multiplier = getChildMultiplier(newParentCode);
      const parentNum = parseInt(newParentCode, 10);
      let childIdx = 0;

      for (const account of siblings) {
        childIdx++;
        const isActualParent = parentCodes.has(account.account_code);
        let newCode: string;
        if (isActualParent) {
          newCode = String(parentNum + childIdx * multiplier);
        } else {
          newCode = String(parentNum + childIdx);
        }
        oldToNew.set(account.account_code, newCode);

        if (isActualParent) {
          renumber(account.account_code, newCode);
        }
      }
    };

    // Top-level: assign codes then recurse
    const topLevel = childrenByParent.get('__root__') || [];
    for (const account of topLevel) {
      const isActualParent = parentCodes.has(account.account_code);
      let newCode: string;
      if (isActualParent) {
        newCode = String(range.start + parentSlot * 100);
        parentSlot++;
      } else {
        newCode = String(range.start + leafSlot);
        leafSlot++;
      }
      oldToNew.set(account.account_code, newCode);
      renumber(account.account_code, newCode);
    }
  }

  // Only include mappings where code actually changes
  const mappings: { old_code: string; new_code: string }[] = [];
  for (const [oldCode, newCode] of oldToNew) {
    if (oldCode !== newCode) {
      mappings.push({ old_code: oldCode, new_code: newCode });
    }
  }
  return mappings;
}
